import { defineModule } from 'direct-vuex'
import api from '@/api'
import Vue from 'vue'
import * as db from '@shared/types/database'
import * as graph from '@shared/types/graphql'
import { moduleActionContext } from '../'
import { ClassicTestType, NewInsight } from '@/utilities/classic-test-types'
import * as ui from '@/types/frontend'
import { WorkspaceWithProjectsQuery, CreateTestMutation } from '@/api/graphql-sdk.generated'
import { validateProject } from '@/utilities/project-helpers'
import store from '@/store'
import analytics from '@/analytics'
import { TemplateSubmissionPayload } from '@/utilities/template-helpers'
import { convertTestTitleUsingCharLimit, testURL } from '@/utilities/test-helpers'
import { pick } from 'lodash'

export interface TestSubmissionPayload {
  payload: TemplateSubmissionPayload | NewInsight
  testType: string
  followUp?: number | null
}

type CreateTestResponse = NonNullable<CreateTestMutation['createTest']>
type TestCreationAudience = Partial<db.Audience> & Pick<db.Audience, 'id' | 'title'>

export const DEFAULT_TEST_TITLE = 'Untitled Test'

export interface TestCreationState {
  workspace: WorkspaceWithProjectsQuery['workspace'] | null
  newProject: ui.ProjectPayload | null
  newProjectError: string | null
  projectPrototypes: graph.Prototype[]
  projects: Pick<graph.Project, 'id' | 'title' | 'ids'>[]
  projectId?: string | null
  audience: TestCreationAudience | null
  section: any
  title: string
  objective: string
  decisionContext: string
  prototypes: Record<string, db.Prototype>
  accountTemplates: graph.Template[]
  selectedTemplateId: number
  testValidForSubmission: boolean
  templateInputValues: Record<string, db.TemplateValue>
  featureConfig: graph.FeatureFlag[]
  acl: graph.PermissionsObject
}

function createInitialState (): TestCreationState {
  return {
    workspace: null,
    newProject: null,
    newProjectError: null,
    projectPrototypes: [],
    projects: [],
    projectId: undefined,
    audience: null,
    section: null,
    title: '',
    objective: '',
    decisionContext: '',
    prototypes: {},
    accountTemplates: [],
    selectedTemplateId: -1,
    templateInputValues: {},
    featureConfig: [],
    testValidForSubmission: false,
    acl: { templateQuestionModification: undefined }
  }
}

const testCreation = defineModule({
  namespaced: true,
  state: createInitialState,
  mutations: {
    setWorkspace (state, workspace: WorkspaceWithProjectsQuery['workspace']) {
      state.workspace = workspace
    },
    setNewProject (state, project: ui.ProjectPayload | null) {
      state.newProject = project
    },
    setNewProjectError (state, error: string | null) {
      state.newProjectError = error
    },
    setProjectPrototypes (state, prototypes: graph.Prototype[]) {
      state.projectPrototypes = prototypes
    },
    setPrototypes (state, prototypes: Record<string, db.Prototype>) {
      state.prototypes = prototypes
    },
    setProjects (state, projects: Pick<graph.Project, 'id' | 'title' | 'ids'>[]) {
      state.projects = projects
    },
    setProjectId (state, projectId: string | null) {
      state.projectId = projectId
    },
    setAudience (state, audience: TestCreationAudience) {
      state.audience = audience
    },
    setSection (state, section) {
      state.section = section
    },
    setFeatureConfig (state, featureConfig) {
      state.featureConfig = featureConfig
    },
    reset (state) {
      Object.assign(state, createInitialState())
    },
    setTitle (state, title) {
      state.title = title
    },
    setObjective (state, objective) {
      state.objective = objective
    },
    setDecisionContext (state, decisionContext) {
      state.decisionContext = decisionContext
    },
    addPrototype (state, { assetId, asset }) {
      Vue.set(state.prototypes, assetId, asset)
    },
    removePrototype (state, id) {
      delete state.prototypes[id]
    },
    setTemplates (state, templates: graph.Template[]) {
      state.accountTemplates = templates
    },
    setSelectedTemplateId (state, id: number) {
      state.selectedTemplateId = id
    },
    setTestValidForSubmission (state, valid: boolean) {
      state.testValidForSubmission = valid
    },
    setAcl (state, acl: graph.PermissionsObject) {
      state.acl = acl
    },
    resetTestType (state) {
      state.prototypes = {}
      state.selectedTemplateId = -1
      state.testValidForSubmission = false
    }
  },
  getters: {
    validTitle ({ title }) {
      return title.trim()
    },
    selectedTemplate (state): graph.Template | undefined {
      return state.accountTemplates
        .find(template => template.id === state.selectedTemplateId)
    },
    currentWorkspaceHasFeature: (state: TestCreationState) => (featureKey: string) => {
      return store.getters.feature.hasFeature(featureKey, state.featureConfig)
    },
    testValidForSubmission (state): boolean {
      return state.testValidForSubmission
    }
  },
  actions: {
    async loadWorkspace (context, workspaceId) {
      const { commit, dispatch } = testCreationActionContext(context)
      const workspace = await api.getWorkspaceWithProjects(workspaceId)
      const featureConfig = await api.getFeatureConfig({ workspaceId, orgId: workspace?.organizationId })
      if (!workspace || !featureConfig) {
        console.error('Error loading workspace or feature flags')
        return
      }
      const { permissions } = await api.getUserAcl(workspace.accountId)
      commit.setWorkspace(workspace)
      commit.setProjects(workspace.projects)
      commit.setProjectId(null)
      commit.setFeatureConfig(featureConfig)
      commit.setAcl(permissions)
      dispatch.fetchTemplates()
    },
    async createAndSetProject (context): Promise<{ continue: boolean, projectId?: string }> {
      const { dispatch, state, commit } = testCreationActionContext(context)
      if (!state.newProject || !state.workspace) return { continue: true }
      const projectError = validateProject(state.newProject)
      if (projectError) {
        commit.setNewProjectError(projectError)
        return { continue: false }
      } else {
        const { title, description } = state.newProject
        const project = await api.createProject(state.workspace.id, title, description)
        dispatch.setProject(project.id!)
        return { continue: true, projectId: project.id! }
      }
    },
    async setProject (context, projectId: string) {
      const { commit, state } = testCreationActionContext(context)
      const workspaceId = state.workspace!.id
      const prototypes = projectId !== null ? await api.getSharedPrototypes(workspaceId, projectId) : []
      commit.setNewProject(null)
      commit.setNewProjectError(null)
      commit.setProjectPrototypes(prototypes)
      commit.setProjectId(projectId)
    },
    setProjectForClonedTest (context, initialTestId: string) {
      const { dispatch, state } = testCreationActionContext(context)
      const { projects } = state

      // Don't need to worry if the cloning was done from within a project
      if (state.projectId) return

      let projectId
      for (let i = 0; i < projects.length; i++) {
        const project = projects[i]
        if (project.ids.includes(initialTestId)) {
          // If test belongs in multiple projects, do nothing
          if (projectId) return
          else projectId = project.id
        }
      }
      if (!projectId) return
      dispatch.setProject(projectId)
    },
    setSelectedAudience (context, { audience }: { audience: TestCreationAudience }) {
      const { commit } = testCreationActionContext(context)
      commit.setAudience(audience)
    },
    clearSelectedAudience ({ commit }) {
      commit('setAudience', null)
    },
    clearStore ({ commit }) {
      commit('clearStore')
    },
    async saveNewTemplate ({ state, rootState }, payload) {
      try {
        const res = await api.createTemplate(state.workspace.id, payload.newTemplatePayload)
        payload.testTemplateId = res.data
        // Remove extra data from payload
        delete payload.newTemplatePayload
        delete payload.saveNewTemplate
        return payload
      } catch (err) {
        console.error('Saving template failed:', err)
      }
    },
    async addTemplateTest (
      context,
      { payload, followUp }: { payload: TemplateSubmissionPayload, followUp?: number | null }
    ): Promise<CreateTestResponse> {
      const { dispatch } = testCreationActionContext(context)

      // Check to see if we need to create the new template first and then append the returned id
      if (payload.saveNewTemplate) {
        payload = await dispatch.saveNewTemplate(payload)
      }
      analytics.track('Test Template', {
        testType: 'template',
        templateTitle: payload.title
      })
      return dispatch.addTest({ payload, testType: 'Template', followUp })
    },
    async addClassicTest (
      context,
      { payload, classicTestType, followUp }: { payload: NewInsight; classicTestType: ClassicTestType; followUp?: number | null }
    ): Promise<CreateTestResponse> {
      const { dispatch } = testCreationActionContext(context)

      return dispatch.addTest({ payload, testType: classicTestType.formName, followUp })
    },
    async addDefaultTest (context): Promise<CreateTestResponse> {
      const { state } = testCreationActionContext(context)
      const newPayload: graph.CreateTestInput = {
        activeviewid: state.projectId ?? undefined,
        testType: '',
        title: DEFAULT_TEST_TITLE,
        objective: '',
        decisionContext: '',
        status: graph.TestStatus.Draft
      }
      const drop = await api.createTest(state.workspace!.id, newPayload)
      return drop
    },
    async addTest (
      context,
      testSubmissionPayload: TestSubmissionPayload
    ): Promise<CreateTestResponse> {
      const { payload, followUp, testType } = testSubmissionPayload
      const { state } = testCreationActionContext(context)
      const { draft, ...rest } = payload
      const newPayload: graph.CreateTestInput = {
        ...rest,
        activeviewid: state.projectId ?? undefined,
        segment: state.audience ? pick(state.audience, 'id', 'title') : undefined,
        testType,
        status: draft ? graph.TestStatus.Draft : graph.TestStatus.InReview
      }
      if (followUp) {
        analytics.track('Follow-up Test Added')
        newPayload.relatedIds = [followUp.toString()]
        newPayload.followUp = followUp
      }
      if (!newPayload.prototypes || !Object.keys(newPayload.prototypes).length) {
        newPayload.prototypes = state.prototypes
      }

      const drop = await api.createTest(state.workspace!.id, newPayload)
      if (testType === 'Template') {
        (payload as TemplateSubmissionPayload).renderedTemplate.forEach((question: db.Question) => {
          if (question.questionType === 'variant') {
            analytics.track('Split Test Enabled', {
              test_id: drop.id,
              experimentId: state.projectId || '',
              test_type: testType,
              template_title: payload.title,
              template_id: (payload as TemplateSubmissionPayload).testTemplateId,
              draft,
              questionId: question.id,
              questionType: question.questionType,
              email: store.state.user.authenticatedUser?.email || '',
              tapId: state.workspace!.id,
              tapName: state.workspace!.name
            })
          }
        })
      }
      return drop
    },
    async cloneTest (context, test: db.Test): Promise<CreateTestResponse> {
      const { state } = testCreationActionContext(context)
      /* eslint-disable camelcase */
      const {
        objective,
        segment,
        title,
        customerQuestions,
        testType,
        lastSubmittedAt,
        activeviewid,
        renderedTemplate,
        testTemplateId,
        templateFieldValues,
        additional_info,
        description,
        decisionContext,
        comparison_type
      } = test.payload

      // Add URL to the decision context to the test this was cloned from
      const originalTestURL = testURL(state.workspace!.id, test.id.toString(), state.projectId)
      let newDecisionContext = `This is a clone of <a href="${originalTestURL}" rel="nofollow">"${title}"</a>`
      // Append old decision context and blank line if it existed
      if (decisionContext) {
        // A little hack-ish... but the redactor appends the \n itself, but needs a <br> to read the html...
        newDecisionContext += `\n<br>\n<br>\n${decisionContext}`
      }

      const newPayload: graph.CreateTestInput = {
        title: convertTestTitleUsingCharLimit(`Clone of ${title}`),
        status: graph.TestStatus.Draft,
        relatedIds: [test.id.toString()],
        followUp: test.id,
        segment,
        objective,
        decisionContext: newDecisionContext,
        customerQuestions,
        prototypes: state.prototypes,
        testType,
        lastSubmittedAt,
        activeviewid,
        renderedTemplate,
        testTemplateId,
        templateFieldValues,
        additional_info,
        description,
        comparison_type
      }
      /* eslint-enable camelcase */
      return api.createTest(test.payload.workspace.id, newPayload)
    },
    setTitle ({ commit }, title) {
      commit('setTitle', title)
    },
    setTestValidForSubmission ({ commit }, valid) {
      commit('setTestValidForSubmission', valid)
    },
    setObjective ({ commit }, objective) {
      commit('setObjective', objective)
    },
    setDecisionContext ({ commit }, decisionContext) {
      commit('setDecisionContext', decisionContext)
    },
    addPrototype (context, { assetId, asset }) {
      const { commit, state, getters } = testCreationActionContext(context)
      const template = getters.selectedTemplate
      analytics.track('Assets Uploaded (Test Creation)', {
        projectId: state.projectId,
        template: template?.title,
        templateId: template?.id,
        usedTemplateTest: !!template
      })
      commit.addPrototype({ assetId, asset })
    },
    removePrototype ({ commit }, assetId) {
      commit('removePrototype', assetId)
    },
    async fetchTemplates (context) {
      const { commit, state } = testCreationActionContext(context)
      if (!state.workspace?.accountId) {
        console.error('No account id')
        return
      }
      const templates = await api.getTemplates(state.workspace.accountId)
      commit.setTemplates(templates)
    },
    resetTestType (context, { testId, workspaceId }) {
      const { commit, rootDispatch } = testCreationActionContext(context)

      const testPayloadUpdate: Partial<db.TestPayload> = {
        testType: '',
        formtype: '',
        description: '',
        additional_info: '',
        prototypes: {},
        comparison_type: '',
        customerQuestions: '',
        testTemplateId: -1,
        templateFieldValues: null
      }

      rootDispatch.workspace.updateTest({
        test: testPayloadUpdate,
        testId: testId,
        workspaceId: workspaceId
      })

      commit.resetTestType()
    },
    reset (context) {
      const { commit } = testCreationActionContext(context)
      commit.reset()
    }
  }
})
export default testCreation
export const testCreationActionContext = (context:any) => moduleActionContext(context, testCreation)
